import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ApolloError } from '@apollo/client';
import {
  getApplicationError,
  getValidationErrors,
  messageFromError
} from '@oysterjs/core/graphql/errors';
import { type ValidationError } from '@oysterjs/core/graphql';

import { Button } from '@oysterjs/ui/button';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@oysterjs/ui/dialog';
import { Spinner } from '@oysterjs/ui/spinner';

import { client } from '~/api/client';
import {
  type CustomPresetInput,
  UpdateCustomPresetDocument,
  type UpdateCustomPresetMutation,
  useGetCustomFormsQuery,
  useGetCustomPresetQuery,
  useGetHtmlDocumentTemplatesQuery,
  useGetWordDocumentTemplatesQuery
} from '~/api/graphql';
import { PresetsCreateOrEditPage, buildTemplateLibrary } from '~/pages/presets/presets';
import { presetNeedsReview } from '~/components/presets/presets.utils';

const GENERIC_SAVE_ERROR = 'Could not save the preset. Please try again.';

/**
 * Completion step: the generated preset is already persisted in state
 * NEEDS_REVIEW, so this is a review-and-refine pass over the normal preset
 * editor, not a create step. Closing without saving loses nothing.
 */
export const ConvertToPresetCompletion = ({
  presetId,
  onClose
}: {
  presetId: string;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = React.useState(false);
  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [error, setError] = React.useState<string | null>(null);

  const presetQuery = useGetCustomPresetQuery({
    variables: { id: presetId },
    fetchPolicy: 'cache-and-network'
  });
  const wordTemplates = useGetWordDocumentTemplatesQuery({ fetchPolicy: 'cache-first' });
  const htmlTemplates = useGetHtmlDocumentTemplatesQuery({ fetchPolicy: 'cache-first' });
  const customForms = useGetCustomFormsQuery({ fetchPolicy: 'cache-first' });

  const preset = presetQuery.data?.customPreset;

  const templateLibrary = React.useMemo(
    () =>
      buildTemplateLibrary({
        wordDocumentTemplates: wordTemplates.data?.wordDocumentTemplates,
        htmlDocumentTemplates: htmlTemplates.data?.htmlDocumentTemplates,
        customForms: customForms.data?.customForms
      }),
    [
      wordTemplates.data?.wordDocumentTemplates,
      htmlTemplates.data?.htmlDocumentTemplates,
      customForms.data?.customForms
    ]
  );

  const handleSubmit = async (form: CustomPresetInput) => {
    setSubmitting(true);
    setValidationErrors([]);
    setError(null);
    try {
      const res = await client.mutate<UpdateCustomPresetMutation>({
        mutation: UpdateCustomPresetDocument,
        variables: { id: presetId, input: form }
      });

      // errorPolicy 'all' lands server errors here rather than rejecting.
      const rejection = new ApolloError({ graphQLErrors: res.errors });
      const validation = getValidationErrors(rejection);

      if (validation?.length) {
        setValidationErrors(validation);
        return;
      }
      if (res.errors?.length) {
        // Never res.errors[0].message: that channel carries raw SQL and bound args.
        setError(getApplicationError(rejection)?.message ?? GENERIC_SAVE_ERROR);
        return;
      }
      if (res.data?.updateCustomPreset?.id) {
        onClose();
        navigate('/app/presets');
      }
    } catch (e) {
      setError(messageFromError(e, GENERIC_SAVE_ERROR));
    } finally {
      setSubmitting(false);
    }
  };

  if (!preset) {
    // Settled without a preset means either a transport error or the house
    // not-found convention (customPreset: null with NO error) — both are
    // terminal here, so show an exit instead of spinning forever.
    const failed = !presetQuery.loading;
    return (
      <Dialog open onClose={onClose} size="lg">
        <DialogTitle>{failed ? 'Preset unavailable' : 'Preparing your preset…'}</DialogTitle>
        <DialogBody>
          {failed ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 m-0">
              The generated preset could not be loaded for review. If generation finished, you can
              find it in Custom Presets marked &ldquo;Needs review&rdquo;.
            </p>
          ) : (
            <div className="flex flex-col items-center gap-3 py-8">
              <Spinner size="size-8" color="sky" />
              <p className="text-sm text-neutral-500 dark:text-neutral-400 m-0">
                Loading the generated preset for review.
              </p>
            </div>
          )}
        </DialogBody>
        {failed && (
          <DialogActions>
            <Button type="button" color="white" onClick={onClose}>
              Close
            </Button>
          </DialogActions>
        )}
      </Dialog>
    );
  }

  return (
    <PresetsCreateOrEditPage
      editMode
      needsReview={presetNeedsReview(preset)}
      initialForm={{
        title: preset.title || '',
        basePrompt: preset.basePrompt || '',
        allowFileUpload: preset.allowFileUpload,
        description: preset.description || '',
        inheritFromPreset: preset.inheritFromPreset || null,
        defaultEffortLevel: preset.defaultEffortLevel ?? null
      }}
      initialPreset={preset}
      onSubmit={(form) => void handleSubmit(form)}
      templateLibrary={templateLibrary}
      validationErrors={validationErrors}
      errorMessage={error}
      onClose={onClose}
      submitting={submitting}
    />
  );
};
